import { createPortal } from "react-dom"
import { useMemo, useRef, useState } from "react"

// ** Third Party Components
import { FolderIcon, FolderOpenIcon, GripVertical } from "lucide-react"
import { useSortable, SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable"
import {
  DndContext,
  DragOverlay,
  DragStartEvent,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
  pointerWithin,
} from "@dnd-kit/core"

// ** UI Components
import { Separator } from "@components/common/separator"

// ** Types
import { DictType } from "@pma/types"

// ** Helpers
import { cn, convertFileSize, getFileDisplayType } from "@pma/utils/helpers"

// ** Custom Hooks
import useTranslator from "@pma/hooks/use-translator"
import { useFilesView } from "@pma/hooks/use-files-view"

// ** Custom Components
import RenderFileIcon from "./render-file-icon"
import FileContextMenu from "./file-context-menu"
import FolderContextMenu from "./folder-context-menu"
import { FilePreview } from "@components/dashboard/preview-file"

export default function FolderTreeSidebar() {
  const { rootFolder, folderItems, setFolderItems } = useFilesView()

  const [activeFileTree, setActiveFileTree] = useState<DictType | null>(null)
  const hoverTimerRef = useRef<NodeJS.Timeout | null>(null)

  const treeSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  )

  const allItemIds = useMemo(() => {
    const ids: string[] = []
    Object.values(folderItems)
      .flat()
      .forEach((item) => {
        if (item.as === "folder") ids.push(`folder_${item.id}`)
        if (item.as === "file") ids.push(`file_${item.id}`)
      })
    return ids
  }, [folderItems])

  function onDragStart(event: DragStartEvent) {
    if (event.active.data.current?.type === "File") {
      setActiveFileTree(event.active.data.current.file)
    }
  }

  function onDragEnd(event: DragEndEvent) {
    const { active, over } = event
    setActiveFileTree(null)
    if (!over || active.id === over.id) return

    const activeId = active.id.toString()
    const activeFolderId = active?.data?.current?.file?.folders_id
    const overId = over.id.toString()

    if (activeId.startsWith("file_") && overId.startsWith("folder_")) {
      const fileId = parseInt(activeId.split("_")[1])
      const folderId = parseInt(overId.split("_")[1])
      const file = Object.values(folderItems)
        .flat()
        .find((i) => i.id === fileId && i.as === "file")
      const targetFolder = Object.values(folderItems)
        .flat()
        .find((i) => i.id === folderId && i.as === "folder")

      if (file && (targetFolder || folderId === rootFolder.id)) {
        const newFolderItems: any = {
          ...folderItems,
          [activeFolderId]: folderItems[activeFolderId].filter((f: any) => f.id !== fileId),
        }
        if (folderItems[folderId] !== undefined) {
          newFolderItems[folderId] = [...folderItems[folderId], file]
        }
        setFolderItems(newFolderItems)
        // moveFileMutation called via parent context
      }
    }

    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current)
      hoverTimerRef.current = null
    }
  }

  function onDragOver(event: any) {
    if (!event.over) return
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current)
  }

  const renderTreeItems = (item: DictType) => {
    if (item.as === "folder") return <TreeFolder key={`folder_${item.id}`} item={item} />
    if (item.as === "file") return <TreeFile key={`file_${item.id}`} item={item} />
    return null
  }

  return (
    <DndContext
      sensors={treeSensors}
      collisionDetection={pointerWithin}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
    >
      <div className='hidden w-[25%] overflow-y-scroll border-r p-4 px-1 md:block md:w-[50%] lg:w-[30%] xl:w-[50%]'>
        <SortableContext items={allItemIds} strategy={verticalListSortingStrategy}>
          {renderTreeItems({ ...rootFolder, as: "folder", level: 0 })}
        </SortableContext>
      </div>

      {createPortal(
        <DragOverlay>
          {activeFileTree && (
            <div className='flex items-center gap-2 rounded-md bg-white p-2 opacity-50 shadow-lg'>
              <RenderFileIcon type={getFileDisplayType(activeFileTree)} />
              <span className='truncate text-sm'>{activeFileTree?.name}</span>
            </div>
          )}
        </DragOverlay>,
        document.body,
      )}
    </DndContext>
  )
}

// --- Tree Folder ---
function TreeFolder({ item }: { item: DictType }) {
  const {
    rootFolder,
    activeFolder,
    folderItems,
    isLoading,
    isFetching,
    handleTreeItemFolderClick,
  } = useFilesView()

  const { locale } = useTranslator()
  const isHebrew = locale === "he"

  const { attributes, listeners, setNodeRef } = useSortable({
    id: `folder_${item.id}`,
    data: { type: "Folder", folder: item },
  })

  const isExpanded = item.id === rootFolder.id ? true : item.isExpanded
  const isActive = activeFolder.id === item.id

  return (
    <div ref={setNodeRef} {...attributes} {...listeners}>
      <div className='folder relative flex flex-col gap-1'>
        <div
          className='flex w-full items-center justify-between'
          style={
            isHebrew
              ? { paddingRight: `${item.level + 1}rem` }
              : { paddingLeft: `${item.level + 1}rem` }
          }
        >
          <button onClick={() => handleTreeItemFolderClick(item)} className='flex-1'>
            <div
              className={cn(
                "flex items-center justify-between gap-1 rounded-md p-1 outline-none transition-colors hover:bg-muted hover:text-black",
                item.isExpanded && "text-accent-foreground",
                isActive && "bg-blue-600 text-white",
              )}
            >
              <div className='flex items-center gap-1'>
                {isExpanded ? (
                  <FolderOpenIcon
                    className={cn("h-5 w-5 text-gray-500", isActive && "text-white")}
                  />
                ) : (
                  <FolderIcon className={cn("h-5 w-5 text-gray-500", isActive && "text-white")} />
                )}
                <div
                  className={cn(
                    "flex flex-1 items-center gap-1 truncate",
                    item.deleted ? "text-red-500" : "",
                  )}
                >
                  {item?.name}
                </div>
              </div>

              {isActive && (isFetching || isLoading) ? (
                <div className='dots-loader pr-3'>
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              ) : (
                <FolderContextMenu item={item} isActive={isActive} />
              )}
            </div>
          </button>
        </div>

        {isExpanded && (
          <div
            className='vertical-line absolute z-10'
            style={{
              top: "2.5rem",
              width: "2px",
              height: "calc(100% - 3.5rem)",
              backgroundColor: "#d1d5db",
              ...(isHebrew ? { right: `${item.level + 1}rem` } : { left: `${item.level + 1}rem` }),
            }}
          />
        )}

        <Separator />

        {isExpanded && folderItems[item.id] && (
          <div className='folder-contents'>
            {folderItems[item.id]
              .sort((a: any, b: any) => (a.as === "folder" && b.as !== "folder" ? -1 : 1))
              .map((child: any) => (
                <div key={child.id} className='relative'>
                  <div
                    className='horizontal-line absolute'
                    style={{
                      ...(isHebrew
                        ? { right: `${item.level + 1}rem` }
                        : { left: `${item.level + 1}rem` }),
                      top: "1.05rem",
                      width: "1rem",
                      height: "2px",
                      backgroundColor: "#d1d5db",
                      transform: "translateY(-50%)",
                    }}
                  />
                  {child.as === "folder" ? (
                    <TreeFolder item={{ ...child, level: item.level + 1 }} />
                  ) : (
                    <TreeFile item={{ ...child, level: item.level + 1 }} />
                  )}
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  )
}

// --- Tree File ---
function TreeFile({ item }: { item: DictType }) {
  const { locale } = useTranslator()
  const isHebrew = locale === "he"

  const { attributes, listeners, setNodeRef } = useSortable({
    id: `file_${item.id}`,
    data: { type: "File", file: item },
  })

  return (
    <div ref={setNodeRef}>
      <FilePreview file={item}>
        <div
          className='flex w-full items-center'
          style={
            isHebrew
              ? { paddingRight: `${item.level + 1}rem` }
              : { paddingLeft: `${item.level + 1}rem` }
          }
        >
          <button className='flex w-full items-center justify-between gap-2 rounded-md p-1 outline-none transition-colors hover:bg-muted'>
            <div className='flex w-full flex-1 items-center gap-2 overflow-hidden'>
              <RenderFileIcon type={getFileDisplayType(item)} />
              <div className={cn("w-[80%] truncate text-left", item.deleted ? "text-red-500" : "")}>
                {item?.name}
              </div>
            </div>
            <div className='flex items-center gap-2'>
              <div className='text-[8px] text-muted-foreground'>{convertFileSize(item.size)}</div>
              <GripVertical
                className='cursor-grab text-gray-500'
                size={20}
                {...listeners}
                onClick={(e) => e.stopPropagation()}
              />
              <FileContextMenu item={item} variant='horizontal' />
            </div>
          </button>
        </div>
      </FilePreview>
    </div>
  )
}
